// 1:1 port from age-of-mcp v3 (src/client/models/v3/ramp.ts) — the flattened
// part literals rebuilt from the original helpers + loops.
//
// v3 ramp — LOGISTICS YELLOW READS INSTANTLY. Authored for the 45° top-down
// camera: a raised steel-grey freight dock whose road-facing edge wears a bold
// HAZARD-striped strip (alternating ROOF_LOGI yellow / HAZARD black blocks —
// the logistics function colour the watcher learns once), a wide sloped
// vehicle apron running down to the road side (+z, the authored front, since
// `orientsToRoad`), yellow kerb cheeks flanking it, a yellow-beamed gantry
// crane spanning the open deck and a stack of cargo crates waiting to ship.
// The deck stays OPEN (no roof) so the loading worker's animation reads from
// above. FOOTPRINT-NATIVE 2x2, authored at its true size, placed at scale 1.
// Like the classic, it authors NO stations — the `load` activity falls back
// to the renderer's stand-at-the-face default.

import { defineModel, styleV3, type Part } from "../../lib";

const { HAZARD, ROOF_LOGI, STEEL, STONE, STONE_DK, WOOD, WOOD_DK, box, cyl, poles, slab } = styleV3;

const BASE     = 0.12;       // foundation top
const DOCK_TOP = 0.5;        // platform top — the working deck
const DOCK_W   = 1.8;        // platform width (x)
const DOCK_D   = 1.3;        // platform depth (z)
const DOCK_CZ  = -0.25;      // platform centre z (the dock sits at the back)
const FRONT_Z  = DOCK_CZ + DOCK_D / 2;   // 0.4 — the apron springs here

// The raised dock platform on the foundation.
function dock(top: number): Part[] {
  return [
    box(DOCK_W, top - BASE, DOCK_D, STONE, [0, (top + BASE) / 2, DOCK_CZ]),
    // A darker plinth course peeking out at the base.
    box(DOCK_W + 0.08, 0.1, DOCK_D + 0.08, STONE_DK, [0, BASE + 0.05, DOCK_CZ]),
  ];
}

// The hazard strip along the dock's loading edge: alternating yellow/black
// blocks, slightly proud of the deck — the icon from above.
function hazardEdge(): Part[] {
  const out: Part[] = [];
  for (let i = 0; i < 6; i++) {
    const x = -0.75 + i * 0.3;
    out.push(box(0.3, 0.03, 0.16, i % 2 === 0 ? ROOF_LOGI : HAZARD,
      [x, DOCK_TOP + 0.015, FRONT_Z - 0.08]));
  }
  return out;
}

// The sloped vehicle apron from the deck edge down to the road, with yellow
// kerb cheeks flanking it.
function apron(): Part[] {
  const rise = DOCK_TOP - 0.04, run = 0.56;
  const len = Math.hypot(rise, run);
  const ang = Math.atan2(rise, run);
  const tilt: [number, number, number] = [-(Math.PI / 2 - ang), 0, 0];
  const cz = FRONT_Z + run / 2;
  return [
    { ...box(1.3, 0.06, len, STEEL, [0, DOCK_TOP / 2, cz]), rotation: tilt },
    { ...box(0.1, 0.08, len, ROOF_LOGI, [-0.7, DOCK_TOP / 2 + 0.03, cz]), rotation: tilt },
    { ...box(0.1, 0.08, len, ROOF_LOGI, [0.7, DOCK_TOP / 2 + 0.03, cz]), rotation: tilt },
  ];
}

// The gantry crane over the deck: two steel posts, a yellow cross beam, and a
// hook block hanging over the loading edge.
function gantry(): Part[] {
  const h = 0.62, gz = -0.55;
  return [
    box(0.1, h, 0.1, STEEL, [-0.8, DOCK_TOP + h / 2, gz]),
    box(0.1, h, 0.1, STEEL, [0.8, DOCK_TOP + h / 2, gz]),
    box(1.76, 0.1, 0.12, ROOF_LOGI, [0, DOCK_TOP + h, gz]),
    box(0.07, 0.2, 0.07, HAZARD, [0.2, DOCK_TOP + h - 0.14, gz]),
  ];
}

// Palletised cargo waiting on the deck.
function cargo(): Part[] {
  return [
    box(0.34, 0.3, 0.34, WOOD, [-0.5, DOCK_TOP + 0.15, -0.6]),
    box(0.3, 0.26, 0.3, WOOD_DK, [-0.12, DOCK_TOP + 0.13, -0.66]),
    box(0.26, 0.24, 0.26, WOOD, [-0.34, DOCK_TOP + 0.42, -0.62]),
    box(0.3, 0.26, 0.3, WOOD, [0.55, DOCK_TOP + 0.13, -0.62]),
  ];
}

// Bumper bollards flanking the apron foot on the road side.
function bollards(): Part[] {
  return [
    cyl(0.05, 0.06, 0.2, 5, ROOF_LOGI, [-0.85, 0.1, 0.92]),
    cyl(0.05, 0.06, 0.2, 5, ROOF_LOGI, [0.85, 0.1, 0.92]),
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(1.94, 1.94)];
  if (s === 1) return [slab(1.94, 1.94), ...dock(0.3)];
  if (s === 2) return [slab(1.94, 1.94), ...dock(DOCK_TOP), ...apron(), ...poles(1.9, 1.9, 0.9)];
  return [
    slab(1.94, 1.94),
    ...dock(DOCK_TOP),
    ...hazardEdge(),
    ...apron(),
    ...gantry(),
    ...cargo(),
    ...bollards(),
  ];
}

export const rampV3 = defineModel({
  id: "ramp-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Ramp (v3)",
  tags: ["building","ramp","low-poly","age-of-mcp","v3"],
  summary: "Low-poly ramp.",
  description: "A raised freight dock.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
